import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { enqueue, isOnline } from "../lib/offlineCart";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [wishlist, setWishlist] = useState({ product_ids: [], products: [] });
  const [coupon, setCouponState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("kb_coupon") || "null"); }
    catch { return null; }
  });

  const setCoupon = useCallback((c) => {
    setCouponState(c);
    if (c) sessionStorage.setItem("kb_coupon", JSON.stringify(c));
    else sessionStorage.removeItem("kb_coupon");
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setCart({ items: [] });
      setWishlist({ product_ids: [], products: [] });
      setCoupon(null);
      return;
    }
    try {
      const [c, w] = await Promise.all([api.get("/cart"), api.get("/wishlist")]);
      setCart(c.data);
      setWishlist(w.data);
    } catch (e) {
      // ignore
    }
  }, [user, setCoupon]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addToCart = async (product_id, size, qty = 1, extras = {}) => {
    const payload = { product_id, size, qty, ...extras };
    const color = extras?.color || null;
    if (!isOnline()) {
      // Optimistic UI: stitch the line into local state so the user sees it
      setCart((prev) => {
        const items = [...(prev?.items || [])];
        const idx = items.findIndex(
          (i) =>
            i.product_id === product_id &&
            (i.size || "") === (size || "") &&
            (i.color || "") === (color || ""),
        );
        if (idx >= 0) items[idx] = { ...items[idx], qty: items[idx].qty + qty };
        else items.push({ product_id, size, color, qty, _pending: true });
        return { items };
      });
      await enqueue({ kind: "add", payload });
      return { items: [] };
    }
    const { data } = await api.post("/cart/items", payload);
    setCart({ items: data.items });
    return data;
  };

  const updateQty = async (product_id, size, qty, color = "") => {
    if (!isOnline()) {
      setCart((prev) => ({
        items: (prev?.items || []).map((i) =>
          i.product_id === product_id &&
          (i.size || "") === (size || "") &&
          (i.color || "") === (color || "")
            ? { ...i, qty, _pending: true }
            : i,
        ),
      }));
      await enqueue({ kind: "update", payload: { product_id, size, qty, color } });
      return;
    }
    const { data } = await api.put(
      `/cart/items/${product_id}`,
      { qty },
      { params: { size: size || "", color: color || "" } },
    );
    setCart({ items: data.items });
  };

  const removeItem = async (product_id, size, color = "") => {
    if (!isOnline()) {
      setCart((prev) => ({
        items: (prev?.items || []).filter(
          (i) =>
            !(
              i.product_id === product_id &&
              (i.size || "") === (size || "") &&
              (i.color || "") === (color || "")
            ),
        ),
      }));
      await enqueue({ kind: "remove", payload: { product_id, size, color } });
      return;
    }
    const { data } = await api.delete(`/cart/items/${product_id}`, {
      params: { size: size || "", color: color || "" },
    });
    setCart({ items: data.items });
  };

  const toggleWishlist = async (product_id) => {
    if (!isOnline()) {
      // Optimistic toggle locally + queue
      setWishlist((prev) => {
        const ids = new Set(prev?.product_ids || []);
        const has = ids.has(product_id);
        if (has) ids.delete(product_id); else ids.add(product_id);
        return { ...prev, product_ids: Array.from(ids) };
      });
      await enqueue({ kind: "wishlist_toggle", payload: { product_id } });
      return !wishlist.product_ids?.includes(product_id);
    }
    const { data } = await api.post(`/wishlist/toggle/${product_id}`);
    await refresh();
    return data.added;
  };

  const inWishlist = (id) => wishlist.product_ids?.includes(id);

  const cartCount = (cart.items || []).reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        wishlist,
        cartCount,
        coupon,
        setCoupon,
        refresh,
        addToCart,
        updateQty,
        removeItem,
        toggleWishlist,
        inWishlist,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
};
